/**
 * V1.5 Topbar — Crumbs links, CmdK-Trigger Mitte, Bell + UserMenu rechts.
 * Quelle: Implementation Brief §7.10 + Screen-Inventory §1.4.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { LanguageSwitcher } from '../language-switcher.js';
import { UserMenu } from '../user-menu.js';
import { HilfeKnopf } from './hilfe/hilfe-knopf.js';

type TopbarProps = {
  onOpenCmdK: () => void;
};

export function Topbar({ onOpenCmdK }: TopbarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const crumbs = buildCrumbs(location.pathname);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenCmdK();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onOpenCmdK]);

  return (
    <header
      className="flex items-center px-5 border-b shrink-0"
      style={{
        height: 'var(--topbar-height)',
        background: 'var(--surface)',
        borderColor: 'var(--line-soft)',
      }}
    >
      {/* Crumbs */}
      <div
        className="flex items-center gap-2 text-xs uppercase tracking-widest"
        style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
      >
        {crumbs.map((c, i) => (
          <span key={c}>
            {c}
            {i < crumbs.length - 1 && <span className="ml-2 opacity-60">/</span>}
          </span>
        ))}
      </div>

      {/* CmdK */}
      <button
        type="button"
        onClick={onOpenCmdK}
        className="ml-auto mr-3 flex items-center gap-2 w-80 px-3 py-1.5 rounded-md text-sm transition-colors hover:bg-[var(--surface-2)]"
        style={{
          background: 'var(--surface-2)',
          color: 'var(--ink-3)',
          border: '1px solid var(--line-soft)',
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" />
        </svg>
        <span className="flex-1 text-left">
          {t('v2.cmdk.placeholder', 'Suchen, springen, ausführen…')}
        </span>
        <kbd
          className="px-1.5 py-0.5 rounded text-[10px]"
          style={{
            background: 'var(--surface-3)',
            color: 'var(--ink-2)',
            fontFamily: 'var(--f-mono)',
          }}
        >
          ⌘K
        </kbd>
      </button>

      <div className="flex items-center gap-3">
        <LanguageSwitcher />
        <HilfeKnopf />
        <div className="w-px h-6" style={{ background: 'var(--line-soft)' }} />
        <UserMenu />
      </div>
    </header>
  );
}

function buildCrumbs(pathname: string): string[] {
  const segments = pathname.split('/').filter(Boolean);
  const root = ['mindsquare', 'projektverwaltung'];
  if (segments.length === 0) return [...root, 'mission control'];
  const labelMap: Record<string, string> = {
    atlas: 'portfolio atlas',
    board: 'phase board',
    staffing: 'staffing',
    projects: 'projekt',
    briefings: 'briefings',
    admin: 'admin',
    einstellungen: 'einstellungen',
    hilfe: 'hilfe',
  };
  const head = segments[0] ?? '';
  const label = labelMap[head] ?? head;
  return [...root, label];
}
